import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { performGetRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import { PACKAGE_NAME, PAGE_SIZE, TEAM } from '../../common/constants';
import type {
	SearchPrioritiesAPIResponse,
	Priority,
	PaginatedPrioritiesData,
} from '../../common/types';
import { refineSearchPriorityAPIResponse } from './utils';

export const SEARCH_PRIORITIES_API = '/rest/api/3/priority/search';

export const mapToPriorities = (
	response: SearchPrioritiesAPIResponse,
): { items: Priority[]; pageSize: number; totalItems: number; isLast: boolean } => ({
	items: response.values,
	pageSize: PAGE_SIZE,
	totalItems: response.total,
	isLast: response.isLast,
});

/**
 * Fetches search priorities from the server.
 * @param pageNumber - The page number to fetch priorities for. It starts with 1.
 * @returns A Promise that resolves to the fetched priorities.
 * @throws If an error occurs during the fetch operation.
 */
export const fetchSearchPrioritiesByPageNumber = async (pageNumber: number) => {
	try {
		const queryParams = new URLSearchParams();
		queryParams.append('startAt', String((pageNumber - 1) * PAGE_SIZE));
		queryParams.append('maxResults', String(PAGE_SIZE));
		queryParams.append('expand', 'schemes');

		const response: unknown = await performGetRequest(
			`${SEARCH_PRIORITIES_API}?${queryParams.toString()}`,
		);

		const searchPriorityApiResponse: SearchPrioritiesAPIResponse =
			refineSearchPriorityAPIResponse(response);

		return mapToPriorities(searchPriorityApiResponse);
	} catch (error) {
		if (error instanceof Error) {
			fireErrorAnalytics({
				error,
				meta: {
					id: 'getSearchPriorities',
					packageName: PACKAGE_NAME,
					teamName: TEAM,
				},
				sendToPrivacyUnsafeSplunk: true,
			}).catch(() => {
				// Ignore error triggered by fireErrorAnalytics
			});
		}
		throw error;
	}
};

// TODO EAX-75: Consider refactor to use a single wider fetch instead of multiple queries, or prevent additional queries from blocking.

/**
 * Fetches search priorities on demand based on the given page number.
 * In order to support re-ordering, we also need the priorities before and after the current page.
 * This method fetches the current page, the next page, and the previous page based on page number.
 * @param pageNumber - The page number to fetch priorities for. It starts with 1.
 * @returns An object containing the fetched priorities, pagination information, and total items.
 */
export const fetchSearchPriorities = async (
	pageNumber: number,
): Promise<PaginatedPrioritiesData> => {
	const currentPriorities = await fetchSearchPrioritiesByPageNumber(pageNumber);
	const nextPriorities = !currentPriorities.isLast
		? await fetchSearchPrioritiesByPageNumber(pageNumber + 1)
		: { items: [], isLast: true };

	const previousPriorities =
		pageNumber > 1
			? await fetchSearchPrioritiesByPageNumber(pageNumber - 1)
			: { items: [], isLast: false };

	const pages: Record<number, { items: Priority[]; isLast: boolean }> = {
		[pageNumber]: { items: currentPriorities.items, isLast: currentPriorities.isLast },
	};

	if (nextPriorities.items.length > 0) {
		pages[pageNumber + 1] = { items: nextPriorities.items, isLast: nextPriorities.isLast };
	}
	if (previousPriorities.items.length > 0) {
		pages[pageNumber - 1] = {
			items: previousPriorities.items,
			isLast: previousPriorities.isLast,
		};
	}

	return {
		pages,
		pageSize: currentPriorities.pageSize,
		totalItems: currentPriorities.totalItems,
	};
};
